import { Colors } from "@/theme/Colors";
import { AppPaletteOptions } from "@/theme/types.ts";

const ENV = import.meta.env.MODE;

const glycemiaPalette = {
  severeHyperglycemia: {
    contrastText: Colors.glycemia.severeHyperglycemia["300"],
    light: Colors.glycemia.severeHyperglycemia["500"],
    main: Colors.glycemia.severeHyperglycemia["700"],
    dark: Colors.glycemia.severeHyperglycemia["900"],
  },
  hyperglycemia: {
    contrastText: Colors.glycemia.hyperglycemia["300"],
    light: Colors.glycemia.hyperglycemia["500"],
    main: Colors.glycemia.hyperglycemia["700"],
    dark: Colors.glycemia.hyperglycemia["900"],
  },
  normal: {
    contrastText: Colors.glycemia.normal["300"],
    light: Colors.glycemia.normal["500"],
    main: Colors.glycemia.normal["700"],
    dark: Colors.glycemia.normal["900"],
  },
  hypoglycemia: {
    contrastText: Colors.glycemia.hypoglycemia["300"],
    light: Colors.glycemia.hypoglycemia["500"],
    main: Colors.glycemia.hypoglycemia["700"],
    dark: Colors.glycemia.hypoglycemia["900"],
  },
  severeHypoglycemia: {
    contrastText: Colors.glycemia.severeHypoglycemia["300"],
    light: Colors.glycemia.severeHypoglycemia["500"],
    main: Colors.glycemia.severeHypoglycemia["700"],
    dark: Colors.glycemia.severeHypoglycemia["900"],
  },
};

const insulinPalette = {
  basal: {
    background: Colors.insulin.basal["300"],
    contrastText: Colors.insulin.basal["300"],
    light: Colors.insulin.basal["500"],
    main: Colors.insulin.basal["700"],
    dark: Colors.insulin.basal["900"],
  },
  bolus: {
    background: Colors.insulin.bolus["300"],
    contrastText: Colors.insulin.bolus["300"],
    light: Colors.insulin.bolus["500"],
    main: Colors.insulin.bolus["700"],
    dark: Colors.insulin.bolus["900"],
  },
};

export const lightPalette: AppPaletteOptions = {
  mode: "light",
  text: {
    title: Colors.primary["900"],
    primary: Colors.neutral["900"],
    secondary: Colors.neutral["500"],
    disabled: Colors.neutral["400"],
  },
  primary: {
    background: Colors.primary["200"],
    light: Colors.primary["500"],
    main: Colors.primary["800"],
    dark: Colors.primary["900"],
    contrastText: Colors.neutral["100"],
  },
  primaryInverted: {
    light: Colors.neutral["900"],
    main: Colors.neutral["100"],
    dark: Colors.neutral["200"],
    contrastText: Colors.primary["800"],
  },
  lightPrimary: {
    background: Colors.primary["200"],
    light: Colors.primary["500"],
    main: Colors.primary["800"],
    dark: Colors.primary["900"],
    contrastText: Colors.neutral["100"],
  },
  secondary: {
    light: Colors.accent["300"],
    main: Colors.accent["500"],
    dark: Colors.accent["900"],
    contrastText: Colors.neutral["900"],
  },
  error: {
    contrastText: Colors.danger["300"],
    light: Colors.danger["500"],
    main: Colors.danger["700"],
    dark: Colors.danger["900"],
  },
  success: {
    contrastText: Colors.success["300"],
    light: Colors.success["500"],
    main: Colors.success["700"],
    dark: Colors.success["900"],
  },
  warning: {
    contrastText: Colors.warning["300"],
    light: Colors.warning["500"],
    main: Colors.warning["700"],
    dark: Colors.warning["900"],
  },
  info: {
    contrastText: Colors.info["300"],
    light: Colors.info["500"],
    main: Colors.info["700"],
    dark: Colors.info["900"],
  },
  grey: Colors.neutral,
  common: {
    black: Colors.black,
    white: Colors.white,
  },
  background: {
    default: Colors.neutral["50"],
    paper: Colors.white,
  },
  green: {
    contrastText: Colors.green["300"],
    light: Colors.green["500"],
    main: Colors.green["700"],
    dark: Colors.green["900"],
  },
  blue: {
    contrastText: Colors.info["300"],
    light: Colors.info["500"],
    main: Colors.info["700"],
    dark: Colors.info["900"],
  },
  orange: {
    background: Colors.orange["100"],
    contrastText: Colors.orange["300"],
    light: Colors.orange["500"],
    main: Colors.orange["600"],
    dark: Colors.orange["900"],
  },
  red: {
    contrastText: Colors.red["300"],
    light: Colors.red["500"],
    main: Colors.red["700"],
    dark: Colors.red["900"],
  },
  pink: {
    contrastText: Colors.pink["300"],
    light: Colors.pink["500"],
    main: Colors.pink["700"],
    dark: Colors.pink["900"],
  },
  violet: {
    contrastText: Colors.violet["300"],
    light: Colors.violet["500"],
    main: Colors.violet["700"],
    dark: Colors.violet["900"],
  },
  neutral: {
    contrastText: Colors.neutral["100"],
    light: Colors.neutral["400"],
    main: Colors.neutral["600"],
    dark: Colors.neutral["800"],
  },
  purple: {
    contrastText: Colors.purple["300"],
    light: Colors.purple["500"],
    main: Colors.purple["600"],
    dark: Colors.purple["900"],
  },
  yellow: {
    contrastText: Colors.yellow["300"],
    light: Colors.yellow["500"],
    main: Colors.yellow["700"],
    dark: Colors.yellow["900"],
  },
  ivory: Colors.ivory,
  environment: Colors[ENV],
  severeHyperglycemia: glycemiaPalette.severeHyperglycemia,
  hyperglycemia: glycemiaPalette.hyperglycemia,
  normalGlycemia: glycemiaPalette.normal,
  hypoglycemia: glycemiaPalette.hypoglycemia,
  severeHypoglycemia: glycemiaPalette.severeHypoglycemia,
  glycemia: glycemiaPalette,
  basalInsulin: insulinPalette.basal,
  bolusInsulin: insulinPalette.bolus,
  insulin: insulinPalette,
  blackAndWhite: {
    background: Colors.white,
    light: Colors.black,
    main: Colors.black,
    dark: Colors.black,
    contrastText: Colors.white,
  },
  agp: {
    p90: Colors.agp.p90,
    p50: Colors.agp.p50,
    median: Colors.agp.median,
  },
  bgm: {
    header: Colors.bgm.header,
    clearColumn: Colors.bgm.clearColumn,
    darkColumn: Colors.bgm.darkColumn,
    orangeDarkBackground: Colors.bgm.orangeDarkBackground,
    redBackground: Colors.bgm.redBackground,
    orangeBackground: Colors.bgm.orangeBackground,
    greenBackground: Colors.bgm.greenBackground,
    purpleText: Colors.bgm.purpleText,
    activityIcon: Colors.bgm.activityIcon,
    purpleBackground: Colors.bgm.purpleBackground,
  },
  tonalOffset: 0.2, // Mui default
  contrastThreshold: 3, // Mui default
  divider: "rgba(0, 0, 0, 0.12)", // Mui default
};
